import { lineStrings } from 'constants/strings';
import { RICH_MESSAGE_IMAGE_MAX_BYTES } from 'constants/richMessageTemplates';
import {
  ERichMenuAreaActionType,
  RICH_MENU_ACTION_LABEL_MAX_LENGTH,
  RICH_MENU_MESSAGE_ACTION_TEXT_MAX_LENGTH,
  RICH_MENU_URI_MAX_LENGTH,
} from 'types';
import { isValidRichMenuUri } from '../RichMenu/Create/validateRichMenuCreate';
import type { TRichMessageAreaAction } from 'types/richMessage';

const msgs = lineStrings.management.richMessage.errors;

export type TRichMessageAreaFieldErrors = {
  uri?: string;
  text?: string;
  label?: string;
};

export type TRichMessageCreateValidationResult = {
  ok: boolean;
  areaErrors: Record<number, TRichMessageAreaFieldErrors>;
  nameError?: string;
  altTextError?: string;
  imageError?: string;
  firstErrorAreaIndex?: number;
};

export function validateRichMessageCreateForm(params: {
  name: string;
  altText: string;
  hasImage: boolean;
  imageFileSize?: number;
  imageWidth?: number;
  customHeightInvalid?: boolean;
  areas: TRichMessageAreaAction[];
}): TRichMessageCreateValidationResult {
  const areaErrors: Record<number, TRichMessageAreaFieldErrors> = {};
  let nameError: string | undefined;
  let altTextError: string | undefined;
  let imageError: string | undefined;

  if (!params.name.trim()) {
    nameError = msgs.nameRequired;
  }

  if (!params.altText.trim()) {
    altTextError = msgs.altTextRequired;
  }

  if (!params.hasImage) {
    imageError = msgs.imageRequired;
  } else if (
    params.imageFileSize !== undefined &&
    params.imageFileSize > RICH_MESSAGE_IMAGE_MAX_BYTES
  ) {
    imageError = msgs.imageTooLarge;
  } else if (params.customHeightInvalid) {
    imageError = msgs.customHeightInvalid;
  }

  params.areas.forEach((action, index) => {
    const field: TRichMessageAreaFieldErrors = {};

    if (action.type === ERichMenuAreaActionType.Uri) {
      const raw = action.uri ?? '';
      const trimmed = raw.trim();
      if (!trimmed) {
        field.uri = msgs.uriRequired;
      } else if (raw.length > RICH_MENU_URI_MAX_LENGTH) {
        field.uri = msgs.uriTooLong;
      } else if (!isValidRichMenuUri(raw)) {
        field.uri = msgs.uriInvalid;
      }
      const label = action.label ?? '';
      if (label.length > RICH_MENU_ACTION_LABEL_MAX_LENGTH) {
        field.label = msgs.labelTooLong;
      }
    }

    if (action.type === ERichMenuAreaActionType.Message) {
      const text = action.text ?? '';
      if (!text.trim()) {
        field.text = msgs.textRequired;
      } else if (text.length > RICH_MENU_MESSAGE_ACTION_TEXT_MAX_LENGTH) {
        field.text = msgs.textTooLong;
      }
    }

    if (Object.keys(field).length > 0) {
      areaErrors[index] = field;
    }
  });

  const ok =
    !nameError &&
    !altTextError &&
    !imageError &&
    Object.keys(areaErrors).length === 0;

  let firstErrorAreaIndex: number | undefined;
  if (!ok && Object.keys(areaErrors).length > 0) {
    firstErrorAreaIndex = Math.min(
      ...Object.keys(areaErrors).map((k) => Number.parseInt(k, 10)),
    );
  }

  return {
    ok,
    areaErrors,
    nameError,
    altTextError,
    imageError,
    firstErrorAreaIndex,
  };
}
